import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

import type {
  DocumentVariableDefinition,
} from '@/lib/documentTemplateVariables';

export type DocumentTemplateType =
  | 'contract'
  | 'power_of_attorney'
  | 'declaration'
  | 'proposal'
  | 'petition'
  | 'defense'
  | 'appeal'
  | 'notice'
  | 'other';

export interface DocumentTemplate {
  id: string;

  name: string;
  slug: string | null;

  documentType:
    DocumentTemplateType;

  legalProductId:
    string | null;

  legalProductName:
    string | null;

  category:
    string | null;

  subcategory:
    string | null;

  legalArea:
    string | null;

  purpose:
    string | null;

  description:
    string | null;

  content: string;

  variables: string[];

  variablesSchema:
    DocumentVariableDefinition[];

  tags: string[];

  isFavorite: boolean;

  currentVersion: number;

  isActive: boolean;

  status:
    | 'active'
    | 'archived';

  notes: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentTemplateInput {
  name: string;

  slug?: string | null;

  documentType:
    DocumentTemplateType;

  legalProductId?:
    string | null;

  category?:
    string | null;

  subcategory?:
    string | null;

  legalArea?:
    string | null;

  purpose?:
    string | null;

  description?:
    string | null;

  content: string;

  variables?:
    string[];

  variablesSchema?:
    DocumentVariableDefinition[];

  tags?:
    string[];

  isFavorite?:
    boolean;

  isActive?:
    boolean;

  notes?:
    string | null;

  changeNote?:
    string | null;
}

export type UpdateDocumentTemplateInput =
  Partial<
    CreateDocumentTemplateInput
  >;

type UseDocumentTemplatesOptions = {
  autoLoad?: boolean;
};

export type LoadDocumentTemplatesOptions = {
  includeArchived?: boolean;
};

const allowedDocumentTypes =
  new Set<
    DocumentTemplateType
  >([
    'contract',
    'power_of_attorney',
    'declaration',
    'proposal',
    'petition',
    'defense',
    'appeal',
    'notice',
    'other',
  ]);

const documentTypeFallback:
  DocumentTemplateType =
    'other';

const toNullableText = (
  value: unknown
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return null;
  }

  const text =
    String(value).trim();

  return text === ''
    ? null
    : text;
};

const toDocumentType = (
  value: unknown
): DocumentTemplateType => {
  const text =
    toNullableText(value);

  if (!text) {
    return documentTypeFallback;
  }

  const normalized =
    text.toLowerCase();

  return allowedDocumentTypes.has(
    normalized as
      DocumentTemplateType
  )
    ? normalized as
        DocumentTemplateType
    : documentTypeFallback;
};

const toBoolean = (
  value: unknown
) => {
  if (
    typeof value ===
    'boolean'
  ) {
    return value;
  }

  if (
    typeof value ===
    'number'
  ) {
    return value === 1;
  }

  if (
    typeof value ===
    'string'
  ) {
    const normalized =
      value
        .trim()
        .toLowerCase();

    if (
      [
        '1',
        'true',
        'active',
        'ativo',
        'sim',
        's',
      ].includes(normalized)
    ) {
      return true;
    }

    if (
      [
        '0',
        'false',
        'inactive',
        'inativo',
        'archived',
        'arquivado',
        'nao',
        'não',
      ].includes(normalized)
    ) {
      return false;
    }
  }

  return Boolean(value);
};

const normalizeStringArray = (
  value: unknown
): string[] => {
  if (Array.isArray(value)) {
    return value
      .map(
        (item) =>
          String(item).trim()
      )
      .filter(Boolean);
  }

  if (
    typeof value ===
    'string'
  ) {
    const trimmed =
      value.trim();

    if (!trimmed) {
      return [];
    }

    try {
      const parsed =
        JSON.parse(trimmed);

      if (
        Array.isArray(parsed)
      ) {
        return normalizeStringArray(
          parsed
        );
      }
    } catch {
      // Continua com fallback textual.
    }

    return trimmed
      .split(/[\n,;]+/g)
      .map(
        (item) =>
          item.trim()
      )
      .filter(Boolean);
  }

  return [];
};

const normalizeVariableSchema = (
  value: unknown
): DocumentVariableDefinition[] => {
  let raw:
    unknown[] = [];

  if (
    Array.isArray(value)
  ) {
    raw = value;
  } else if (
    typeof value ===
      'string' &&
    value.trim()
  ) {
    try {
      const parsed =
        JSON.parse(value);

      if (
        Array.isArray(parsed)
      ) {
        raw = parsed;
      }
    } catch {
      raw = [];
    }
  }

  return raw
    .map(
      (item) => {
        if (
          !item ||
          typeof item !==
            'object'
        ) {
          return null;
        }

        const object =
          item as
            Record<
              string,
              unknown
            >;

        const key =
          String(
            object.key ??
              ''
          ).trim();

        if (!key) {
          return null;
        }

        const group =
          String(
            object.group ??
              'manual'
          ) as
            DocumentVariableDefinition['group'];

        return {
          key,

          label:
            String(
              object.label ??
                key
            ).trim() ||
            key,

          group,

          required:
            object.required ===
            true,
        };
      }
    )
    .filter(
      (
        item
      ): item is
        DocumentVariableDefinition =>
        Boolean(item)
    );
};

const normalizeDocumentTemplate =
  (
    value: any
  ): DocumentTemplate => {
    const isActive =
      toBoolean(
        value?.isActive ??
          value?.is_active ??
          value?.status
      );

    const currentVersion =
      Number(
        value?.currentVersion ??
          value?.current_version ??
          1
      );

    return {
      id:
        String(
          value?.id ?? ''
        ),

      name:
        String(
          value?.name ?? ''
        ).trim() ||
        'Sem nome',

      slug:
        toNullableText(
          value?.slug
        ),

      documentType:
        toDocumentType(
          value?.documentType ??
            value?.document_type
        ),

      legalProductId:
        toNullableText(
          value?.legalProductId ??
            value?.legal_product_id
        ),

      legalProductName:
        toNullableText(
          value?.legalProductName ??
            value?.legal_product_name
        ),

      category:
        toNullableText(
          value?.category
        ),

      subcategory:
        toNullableText(
          value?.subcategory
        ),

      legalArea:
        toNullableText(
          value?.legalArea ??
            value?.legal_area
        ),

      purpose:
        toNullableText(
          value?.purpose
        ),

      description:
        toNullableText(
          value?.description
        ),

      content:
        String(
          value?.content ?? ''
        ),

      variables:
        normalizeStringArray(
          value?.variables
        ),

      variablesSchema:
        normalizeVariableSchema(
          value?.variablesSchema ??
            value?.variables_schema
        ),

      tags:
        normalizeStringArray(
          value?.tags
        ),

      isFavorite:
        toBoolean(
          value?.isFavorite ??
            value?.is_favorite
        ),

      currentVersion:
        Number.isFinite(
          currentVersion
        ) &&
        currentVersion >= 1
          ? currentVersion
          : 1,

      isActive,

      status:
        isActive
          ? 'active'
          : 'archived',

      notes:
        toNullableText(
          value?.notes
        ),

      createdAt:
        toNullableText(
          value?.createdAt ??
            value?.created_at
        ) ??
        new Date()
          .toISOString(),

      updatedAt:
        toNullableText(
          value?.updatedAt ??
            value?.updated_at
        ) ??
        new Date()
          .toISOString(),
    };
  };

const sortDocumentTemplates = (
  a: DocumentTemplate,
  b: DocumentTemplate
) =>
  Number(b.isFavorite) -
    Number(a.isFavorite) ||
  b.updatedAt.localeCompare(
    a.updatedAt
  ) ||
  a.name.localeCompare(
    b.name,
    'pt-BR'
  );

const sortAndSet = (
  setDocumentTemplates:
    Dispatch<
      SetStateAction<
        DocumentTemplate[]
      >
    >,
  next:
    DocumentTemplate[]
) => {
  setDocumentTemplates(
    [...next].sort(
      sortDocumentTemplates
    )
  );
};

export function useDocumentTemplates(
  options:
    UseDocumentTemplatesOptions = {}
) {
  const {
    autoLoad = true,
  } = options;

  const [
    documentTemplates,
    setDocumentTemplates,
  ] =
    useState<
      DocumentTemplate[]
    >([]);

  const [
    isLoadingDocumentTemplates,
    setIsLoadingDocumentTemplates,
  ] =
    useState(true);

  const [
    documentTemplatesError,
    setDocumentTemplatesError,
  ] =
    useState<
      string | null
    >(null);

  const requestIdRef =
    useRef(0);

  const loadDocumentTemplates =
    useCallback(
      async (
        loadOptions:
          LoadDocumentTemplatesOptions = {}
      ) => {
        const requestId =
          ++requestIdRef.current;

        setIsLoadingDocumentTemplates(
          true
        );

        setDocumentTemplatesError(
          null
        );

        try {
          const searchParams =
            new URLSearchParams();

          if (
            loadOptions.includeArchived
          ) {
            searchParams.set(
              'includeArchived',
              '1'
            );
          }

          const endpoint =
            `/api/document-templates${
              searchParams.toString()
                ? `?${searchParams.toString()}`
                : ''
            }`;

          const response =
            await fetchWithRetry(
              endpoint
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.documentTemplates
            )
          ) {
            throw new Error(
              data.error ||
                `GET /api/document-templates falhou com status ${response.status}`
            );
          }

          if (
            requestId ===
            requestIdRef.current
          ) {
            sortAndSet(
              setDocumentTemplates,
              data.documentTemplates.map(
                normalizeDocumentTemplate
              )
            );
          }
        } catch (error) {
          console.error(
            'Erro ao carregar modelos de documento.',
            error
          );

          if (
            requestId ===
            requestIdRef.current
          ) {
            setDocumentTemplatesError(
              'Não foi possível carregar os modelos reais.'
            );
          }
        } finally {
          if (
            requestId ===
            requestIdRef.current
          ) {
            setIsLoadingDocumentTemplates(
              false
            );
          }
        }
      },
      []
    );

  const createDocumentTemplate =
    useCallback(
      async (
        input:
          CreateDocumentTemplateInput
      ) => {
        const response =
          await fetchWithRetry(
            '/api/document-templates',
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.documentTemplate
        ) {
          throw new Error(
            data.error ||
              `POST /api/document-templates falhou com status ${response.status}`
          );
        }

        const created =
          normalizeDocumentTemplate(
            data.documentTemplate
          );

        setDocumentTemplates(
          (prev) =>
            [
              created,
              ...prev.filter(
                (item) =>
                  item.id !==
                  created.id
              ),
            ].sort(
              sortDocumentTemplates
            )
        );

        return created;
      },
      []
    );

  const updateDocumentTemplate =
    useCallback(
      async (
        id: string,
        input:
          UpdateDocumentTemplateInput
      ) => {
        const response =
          await fetchWithRetry(
            `/api/document-templates/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'PUT',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.documentTemplate
        ) {
          throw new Error(
            data.error ||
              `PUT /api/document-templates/${id} falhou com status ${response.status}`
          );
        }

        const updated =
          normalizeDocumentTemplate(
            data.documentTemplate
          );

        setDocumentTemplates(
          (prev) =>
            [
              updated,
              ...prev.filter(
                (item) =>
                  item.id !==
                  updated.id
              ),
            ].sort(
              sortDocumentTemplates
            )
        );

        return updated;
      },
      []
    );

  const deleteDocumentTemplate =
    useCallback(
      async (
        id: string
      ) => {
        const response =
          await fetchWithRetry(
            `/api/document-templates/${encodeURIComponent(
              id
            )}`,
            {
              method:
                'DELETE',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              `DELETE /api/document-templates/${id} falhou com status ${response.status}`
          );
        }

        setDocumentTemplates(
          (prev) =>
            prev.filter(
              (item) =>
                item.id !== id
            )
        );
      },
      []
    );

  useEffect(
    () => {
      if (!autoLoad) {
        return;
      }

      void loadDocumentTemplates();
    },
    [
      autoLoad,
      loadDocumentTemplates,
    ]
  );

  return {
    documentTemplates,

    isLoadingDocumentTemplates,

    documentTemplatesError,

    loadDocumentTemplates,

    createDocumentTemplate,

    updateDocumentTemplate,

    deleteDocumentTemplate,
  };
}